import React, {
  useContext,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import UserContext from "../../context/user/UserContext";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import formatter from "../../modules/formatter";
import { useQuery } from "../Hooks";
import {
  getQuotes,
  copyQuote,
  deleteQuote,
} from "../../requests/quoteManagementRequests";
import Table from "../UI/Table";
import { PageWrapper } from "../UI/Grid";
import { Body, Heading } from "../UI/Text";
import { Flex } from "reflexbox";
import styled, { useTheme } from "styled-components";
import Button from "../UI/Button";
import Loading from "../UI/Loading";
import AlertContext from "../../context/alert/AlertContext";
import Card from "../UI/Card";
import Modal from "../UI/Modal";
import _ from "lodash";
import { createRMAFromQuote } from "../../requests/RMAManagementRequests";
// import { getStatusTypes } from "../../requests/statusTypesMgmtReqs";

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

const OrderStatus = ({ csiStatus: _status, quoteId }) => {
  const statusTypes = {
    P: "Planned",
    S: "Stopped",
    C: "Complete",
    O: "Ordered",
    H: "History",
  }; //W C S O

  let csiStatus = _status;

  const { platform, activeOrderStatus, queries } = useContext(UserContext);
  const { csiOrderCompleteString } = platform || {};

  if (activeOrderStatus?.length > 0) {
    activeOrderStatus.forEach((status) => {
      const { swiftsellQuoteId, csiStatus: csi } = status;
      if (swiftsellQuoteId === quoteId) csiStatus = csi;
      return queries?.myOrders?.rows?.forEach((row, i) => {
        if (row?.id === swiftsellQuoteId)
          queries.myorders.rows[i].csiStatus = csi;
      });
    });
  }

  return (
    <Flex width={1}>
      <Body center>
        {statusTypes[csiStatus] || csiOrderCompleteString || "Ordered"}
      </Body>
    </Flex>
  );
};

const OrdersPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeUser, loggedIn, numOrdersUpdated, pid } =
    useContext(UserContext);
  const { username, groupId } = activeUser || {};
  const { setQuote } = useContext(ConfiguratorContext);
  const { pathname = "/" } = location || {};
  const { 1: path } = pathname.split("/");

  const isMyOrders = useMemo(() => path === "MyOrders", [path]);

  const queryID = useMemo(
    () => (isMyOrders ? "myOrders" : "allOrders"),
    [isMyOrders]
  );

  const myOrdersRes = useQuery({
    queryID,
    request: getQuotes,
    queryVariables: {
      username: !isMyOrders ? undefined : groupId ? undefined : username,
      type: 2,
      groupId: !isMyOrders ? undefined : groupId,
      pid,
    },
    skip: !loggedIn || (isMyOrders && !username) || !pid,
  });

  // const { data: stData } = useQuery({
  //   queryID: "statusTypes",
  //   request: getStatusTypes,
  //   forcedFilters: {},
  //   attributes: ["id", "name"],
  //   skip: !loggedIn || !isMyOrders || !username,
  // });

  // const { rows: statusTypes } = stData || {};

  const { data, isLoading, refetchQuery, setData } = myOrdersRes || {};
  const [isCopying, setIsCopying] = useState(false);
  const [show, setShow] = useState(false);
  const [currentQuote, setCurrentQuote] = useState(false);

  const { showWarningAlert, showSuccessAlert, showFailureAlert } =
    useContext(AlertContext);

  useEffect(() => {
    if (numOrdersUpdated > 0) refetchQuery(queryID);
  }, [numOrdersUpdated, queryID, refetchQuery]);

  const { rows: quotes = [], count: totalCount } = data || {};

  const onCopyQuote = useCallback(
    async (event, quoteId) => {
      event.stopPropagation();
      try {
        setIsCopying(quoteId);
        showWarningAlert(`Copying Order #${quoteId} to a new Quote...`);

        const newQuote = await copyQuote({ id: quoteId });

        refetchQuery(queryID);

        setIsCopying(false);
        showSuccessAlert(
          `New Quote #${newQuote.id} copied from Order #${quoteId}.`
        );
        navigate(`/Quote/${newQuote?.id}`);
      } catch (error) {
        console.error(error);
        showFailureAlert(`${error}`);
      }
    },
    [
      navigate,
      queryID,
      refetchQuery,
      showFailureAlert,
      showSuccessAlert,
      showWarningAlert,
    ]
  );

  const onSecondaryClick = useCallback(async (e, quoteId) => {
    e.stopPropagation();
    setShow(true);
    setCurrentQuote(quoteId);
  }, []);

  const onDealerPricingClick = useCallback(() => {
    setCurrentQuote(null);
    navigate(`/DealerQuote/${currentQuote}`);
  }, [currentQuote, navigate]);

  const subtitle = useMemo(() => {
    let _subtitle = isMyOrders
      ? "Here you can view all of your previously placed orders."
      : "Here you can view previously placed orders for all of your registered users and administrators.";
    _subtitle +=
      " No need to refresh this page, any updates to the order status will be updated automatically.";
    return _subtitle;
  }, [isMyOrders]);

  const onDelete = useCallback(
    async (e, id) => {
      e.stopPropagation();
      const newQuotes = [...quotes];
      _.remove(newQuotes, ({ id: QuoteID }) => QuoteID === id);
      const newCount = totalCount - 1;
      setData({ rows: newQuotes, count: newCount });

      await deleteQuote({ id });
    },
    [quotes, totalCount, setData]
  );

  const onCreateRMA = useCallback(
    async (e, id) => {
      e.stopPropagation();
      const { id: rmaId } = await createRMAFromQuote(id);
      if (rmaId) return navigate(`/RMA/${rmaId}`);
      return showFailureAlert("Error creating RMA from Order.");
    },
    [navigate, showFailureAlert]
  );

  const columns = useMemo(
    () => [
      // {
      //   id: "erpOrderNo",
      //   data: (row) => <Body center>{row?.erpOrderNo}</Body>,
      //   header: "Order #",
      //   size: { md: 2, sm: 2, xs: 2 },
      // },

      {
        id: "companyName",
        data: (row) => {
          const { lastName, firstName, companyName } = row;

          return (
            <Body center>
              {companyName
                ? companyName
                : `${firstName || ""}${lastName && firstName ? " " : ""}${
                    lastName || " "
                  }`}
            </Body>
          );
        },
        header: "Customer",
        size: { md: 3, sm: 3, xs: -1 },
      },

      {
        id: "createdByFirstName",
        data: (row) => (
          <Body ellipsis center>
            {row?.createdByFirstName} {row?.createdByLastName}
          </Body>
        ),
        header: "Created By",
        size: { md: 2, sm: 2, xs: -1 },
      },
      {
        id: "dateOrderPlaced",
        data: (row) => (
          <Body center>{`${moment(row.dateOrderPlaced).format(
            "MM/DD/YYYY"
          )}`}</Body>
        ),
        header: "Ordered On",
        size: { md: 2, sm: 2, xs: 4 },
      },
      // {
      //   id: "csiStatus",
      //   data: ({ csiStatus, id }) => (
      //     <OrderStatus quoteId={id} csiStatus={csiStatus} />
      //   ),
      //   header: "Status",
      //   sort: false,
      //   size: { md: 1, sm: 0, xs: 0 },
      // },
      {
        id: "totalCost",
        data: (row) => (
          <Body right>{`$${formatter.currency(row.totalCost)}`}</Body>
        ),
        header: "Price",
        size: { md: 2, sm: 0, xs: 0 },
      },
      {
        display: (props) => {
          let quote = props.cell.row.original;

          return (
            <ButtonsWrapper id="actions">
              {isCopying === props.cell.row.original.id ? (
                <Loading inline />
              ) : (
                <>
                  {/* <Button
                    size="small"
                    reverse
                    title="Create RMA for Order"
                    iconName="faPersonWalkingArrowLoopLeft"
                    onClick={(e) => onCreateRMA(e, quote.id)}
                  /> */}
                  <Button
                    size="small"
                    color={theme.success}
                    reverse
                    title="Copy Order to new Quote"
                    iconName="faCopy"
                    onClick={(e) => onCopyQuote(e, quote.id)}
                  />
                  {activeUser?.roleLevel === 9 && (
                    <Button
                      size="small"
                      color={theme.danger}
                      reverse
                      title="Delete Order"
                      iconName="faTrash"
                      onClick={(e) => onDelete(e, quote.id)}
                    />
                  )}
                </>
              )}
            </ButtonsWrapper>
          );
        },
        header: "",
        sort: false,
        size: { md: 3, sm: 1, xs: 1 },
      },
    ],
    [
      activeUser,
      isCopying,
      onCopyQuote,
      onCreateRMA,
      onDelete,
      theme.danger,
      theme.success,
    ]
  );

  const onRowClick = (row) => {
    const { original: quote } = row || {};
    setQuote({ ...quote });

    navigate(`/Quote/${quote?.id}`);
  };

  return ( 
    <>
      <Modal show={show} setShow={setShow}>
        <Card maxWidth="400px">
          <Heading bold center color={theme.danger}>
            INTERNAL DOCUMENTS
          </Heading>
          <Heading my="1rem" center>
            Are you sure you want to view this page?
          </Heading>
          <Body center>
            This page contains sensitive internal documents, are you sure you
            wish to proceed?
          </Body>
          <Flex my="1rem">
            <Button
              iconName="faBan"
              color={theme.danger}
              onClick={() => setShow(false)}
            >
              Go Back
            </Button>
            <Button
              color={theme.success}
              iconName="faCheck"
              onClick={onDealerPricingClick}
            >
              Continue
            </Button>
          </Flex>
        </Card>
      </Modal>
      <PageWrapper>
        <Table
          title={isMyOrders ? "Orders" : "All Orders"}
          backButtonLink={isMyOrders ? undefined : "/Administration"}
          hideCreate
          hideRefetch={false}
          refetchData={refetchQuery}
          isLoading={isLoading}
          subtitle={subtitle}
          columns={columns}
          data={quotes}
          dropdown={false}
          hideToggle
          totalCount={totalCount}
          onRowClick={onRowClick}
        />
      </PageWrapper>
    </>
  );
};

export default OrdersPage;
